import React, { forwardRef } from 'react';

import TouchableLink from '@skippay/_current/components/TouchableLink';
import { createHrefWithRedirect } from '@skippay/core/utils/routeUtils';

import { ButtonComponent as ButtonComponentProps, ButtonRef } from './@types/button';

/**
 * Underlying `<button/>` component
 */
const TouchableButton = forwardRef(function TouchableButton(
	props: ButtonComponentProps,
	ref: React.RefObject<HTMLButtonElement>
) {
	return <button type="button" {...props} ref={ref} />;
});

const ButtonComponent = forwardRef(
	(
		props: ButtonComponentProps,
		ref: React.RefObject<ButtonRef<ButtonComponentProps['component'], ButtonComponentProps['to']>>
	): JSX.Element => {
		const {
			component: Component,
			to,
			onClick,
			onPopupBrowserClose,
			openInPopupBrowser,
			shareUserSession,
			shouldClosePopupBrowser,
			isExternal,
			...otherProps
		} = props;

		if (isExternal && to) {
			return (
				<a
					{...otherProps}
					href={createHrefWithRedirect(to)}
					target="_blank"
					rel="noopener noreferrer"
					ref={ref}
				>
					{props.children}
				</a>
			);
		}

		if (props.component) {
			return <Component {...props} ref={ref} />;
		}
		if (to) {
			return props.disabled ? (
				<button type="button" {...props} ref={ref} />
			) : (
				<TouchableLink
					{...otherProps}
					onClick={onClick}
					to={to}
					ref={ref}
					{...(process.env.MOB_APP_BUILD
						? { onPopupBrowserClose, openInPopupBrowser, shareUserSession }
						: { shouldClosePopupBrowser })}
				/>
			);
		}
		if (onClick) {
			return <TouchableButton {...props} ref={ref} />;
		}
		if (props.type === 'submit') {
			return <button name="submit" type="submit" {...props} ref={ref} />;
		}
		return <button type="button" {...props} ref={ref} />;
	}
);

export default ButtonComponent;
